/**
 * Offline write interceptor for axios.
 *
 * When an eligible mutation (POST/PATCH on packing / journal / places /
 * reservations under `/api/trips/{id}/…`) fails with a network error OR
 * `navigator.onLine === false`, we:
 *
 *   1. enqueue it in IndexedDB (`offlineOutbox.enqueue`)
 *   2. resolve the axios call with a synthetic 202 response so the caller
 *      can update its UI optimistically instead of showing a red error
 *   3. return the local record with `_local: true` so components can flag
 *      pending items with a subtle UI hint
 *
 * Kill switch — the interceptor is entirely bypassed when
 * `window.__tovias_offline_write_disabled === true` (set from the backend
 * health header, so we can disable this feature in prod with a single env
 * variable if something goes wrong).
 */
import { enqueue } from './offlineOutbox';

// Whitelist regexes — kept intentionally narrow for the MVP. Anything not
// on this list falls through to axios' normal error handling (i.e. same
// behaviour as before this feature existed).
const ELIGIBLE_PATH = /\/api\/trips\/[^/]+\/(packing|journal|places|reservations)(\b|\/)/;

const ELIGIBLE_METHODS = new Set(['POST', 'PATCH', 'PUT']);

// Resource-specific fields we need to auto-generate a client-side UUID for
// creates. The server accepts any incoming `id`; if absent, it generates
// one. By sending our own we get consistency between the optimistic local
// record and the eventual server record. See PRD note 2026-02-04.
function ensureIdInBody(url, body) {
  if (!body || typeof body !== 'object') return body;
  if (body.id) return body;
  // Only creates need a client-side id; PATCH targets an existing id in URL.
  return body;
}

function isNetworkError(err) {
  // Axios raises err.code === 'ERR_NETWORK' when fetch() throws (typically
  // DNS/connection refused/offline). Some proxies also produce ECONNABORTED
  // on timeout. Both are safe to enqueue.
  if (!err) return false;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  // Axios older versions: !err.response means the request never got a reply
  if (!err.response && (err.message || '').toLowerCase().includes('network')) return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return false;
}

function resourceFromUrl(url) {
  const m = ELIGIBLE_PATH.exec(url || '');
  return m ? m[1] : null;
}

function tripIdFromUrl(url) {
  const m = /\/api\/trips\/([^/]+)/.exec(url || '');
  return m ? m[1] : null;
}

/** Install the interceptor. Idempotent — safe to call multiple times. */
export function installOfflineWriteInterceptor(axios) {
  if (axios.__tovias_offline_installed) return;
  axios.__tovias_offline_installed = true;

  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      if (typeof window !== 'undefined' && window.__tovias_offline_write_disabled) {
        return Promise.reject(error);
      }
      const cfg = error?.config;
      if (!cfg) return Promise.reject(error);
      const method = String(cfg.method || '').toUpperCase();
      if (!ELIGIBLE_METHODS.has(method)) return Promise.reject(error);
      const url = cfg.url || '';
      if (!ELIGIBLE_PATH.test(url)) return Promise.reject(error);
      if (!isNetworkError(error)) return Promise.reject(error);

      // Try to enqueue.
      try {
        const body = cfg.data
          ? (typeof cfg.data === 'string' ? JSON.parse(cfg.data) : cfg.data)
          : undefined;
        const rec = await enqueue({
          method,
          url,
          body: ensureIdInBody(url, body),
          resource: resourceFromUrl(url),
          tripId: tripIdFromUrl(url),
          action: method === 'POST' ? 'create' : 'update',
        });
        // Return a synthetic 202 with the payload the caller was about to
        // send. This lets React components treat it as "saved locally" and
        // insert an optimistic row in their state.
        return {
          status: 202,
          statusText: 'Queued (offline)',
          data: {
            ...(rec.body || {}),
            id: (rec.body && rec.body.id) || rec.id,
            _local: true,
            _outbox_id: rec.id,
          },
          headers: { 'x-offline-queued': '1' },
          config: cfg,
          request: null,
        };
      } catch (enqErr) {
        // IndexedDB unavailable → let the caller see the original error.
        return Promise.reject(error);
      }
    },
  );
}
